import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLiveRefresh } from "@/contexts/LiveRefresh";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import Attachments from "@/components/Attachments";
import { Loader2, MessageSquare, Send, Pencil, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";

function initials(name) {
    return (name || "?")
        .split(" ")
        .map((p) => p[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
}

export default function TaskComments({ taskId }) {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [body, setBody] = useState("");
    const [posting, setPosting] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editBody, setEditBody] = useState("");

    const load = async () => {
        if (!taskId) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/tasks/${taskId}/comments`);
            setComments(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [taskId]);
    useLiveRefresh(load);

    const post = async () => {
        if (!body.trim()) return;
        setPosting(true);
        try {
            await api.post(`/tasks/${taskId}/comments`, { body: body.trim() });
            setBody("");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setPosting(false);
        }
    };

    const saveEdit = async (c) => {
        if (!editBody.trim()) return;
        try {
            await api.patch(`/comments/${c.id}`, { body: editBody.trim() });
            setEditingId(null);
            setEditBody("");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const remove = async (c) => {
        try {
            await api.delete(`/comments/${c.id}`);
            toast.success("Comment deleted");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="mt-6 rounded-lg border border-border bg-card p-4" data-testid="comments-section">
            <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold">Comments</span>
                {comments.length > 0 && (
                    <span className="text-xs text-muted-foreground">({comments.length})</span>
                )}
            </div>

            {/* Add comment */}
            <div className="mt-3 flex gap-3">
                <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                    {initials(user?.name)}
                </div>
                <div className="flex-1">
                    <Textarea
                        rows={2}
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        placeholder="Add a comment..."
                        data-testid="comment-input"
                    />
                    <div className="mt-2 flex justify-end">
                        <Button
                            type="button"
                            size="sm"
                            onClick={post}
                            disabled={posting || !body.trim()}
                            data-testid="comment-post"
                        >
                            {posting ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <>
                                    <Send className="mr-1.5 h-4 w-4" /> Comment
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Thread */}
            {loading ? (
                <div className="grid place-items-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                </div>
            ) : comments.length === 0 ? (
                <p className="mt-3 text-xs text-muted-foreground">
                    No comments yet. Start the conversation — comments are visible to the whole team.
                </p>
            ) : (
                <ul className="mt-4 space-y-4">
                    {comments.map((c) => (
                        <li key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
                            <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-secondary text-xs font-semibold text-foreground">
                                {initials(c.author_name)}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 text-xs">
                                    <span className="font-semibold text-foreground">
                                        {c.author_name}
                                    </span>
                                    <span className="text-muted-foreground">
                                        {new Date(c.created_at).toLocaleString("en-IN")}
                                    </span>
                                    {c.edited && (
                                        <span className="text-muted-foreground">(edited)</span>
                                    )}
                                    {isAdmin && editingId !== c.id && (
                                        <span className="ml-auto flex items-center gap-1">
                                            <button
                                                type="button"
                                                className="text-muted-foreground hover:text-primary"
                                                onClick={() => { setEditingId(c.id); setEditBody(c.body); }}
                                                data-testid={`comment-edit-${c.id}`}
                                            >
                                                <Pencil className="h-3.5 w-3.5" />
                                            </button>
                                            <button
                                                type="button"
                                                className="text-muted-foreground hover:text-red-600"
                                                onClick={() => remove(c)}
                                                data-testid={`comment-delete-${c.id}`}
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        </span>
                                    )}
                                </div>

                                {editingId === c.id ? (
                                    <div className="mt-1">
                                        <Textarea
                                            rows={2}
                                            value={editBody}
                                            onChange={(e) => setEditBody(e.target.value)}
                                            data-testid={`comment-edit-input-${c.id}`}
                                        />
                                        <div className="mt-1 flex gap-1">
                                            <Button type="button" size="sm" onClick={() => saveEdit(c)}>
                                                <Check className="mr-1 h-3.5 w-3.5" /> Save
                                            </Button>
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => { setEditingId(null); setEditBody(""); }}
                                            >
                                                <X className="mr-1 h-3.5 w-3.5" /> Cancel
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="mt-0.5 whitespace-pre-wrap text-sm text-foreground">
                                        {c.body}
                                    </div>
                                )}

                                {/* Per-comment attachments */}
                                <Attachments taskId={taskId} commentId={c.id} compact />
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
